import { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BottomSheet, Dialog, type BottomSheetProps } from './BottomSheet';
import { StudioContext } from '../shell/Studio';

export type AsideProps = BottomSheetProps & {
  /** Panel heading on the desk (the phone sheet supplies its own Dialog.Title). */
  title?: string;
};

/**
 * A SHEET ON THE PHONE, A PANEL ON THE DESK (spec §4.3). Below the studio tiers this is
 * `BottomSheet`, props forwarded untouched — focus trap, Escape, swipe-dismiss, restoreFocusRef all
 * exactly as before, so a converted sheet's own tests keep passing without edits. On a studio tier
 * it portals its children into the enclosing <Studio>'s right slot, inside a NON-MODAL
 * `Dialog.Root` and nothing else: Radix's `Dialog.Title` / `Dialog.Close` / `Dialog.Description`
 * only need Root's context (verified against @radix-ui/react-dialog 1.1.23: DialogTitle reads
 * `useDialogContext(TITLE_NAME)`, i.e. the Root provider), so a sheet body written against
 * BottomSheet's re-exported `Dialog` renders unchanged as a panel. No Portal/Overlay/Content means
 * no focus trap and no dim — a panel is part of the page, not over it.
 *
 * The open flag is the ONLY thing written into shared state, from an effect keyed on `open`, so a
 * <StrictMode> double-invoke writes the same claim twice (idempotent; CLAUDE.md Lesson 1). The
 * claim is per-instance (a stable `useRef({})` token), not a bare shared boolean: two Asides can be
 * mounted under one Studio at once, and a sibling toggling closed or unmounting must not clobber
 * another sibling's own still-open claim on the right slot (see docs/RULINGS.md).
 *
 * Fix sweep 2026-09-19 (the Studio's three shared `Aside` items):
 * - `restoreFocusRef` is honoured in PANEL mode too. BottomSheet's Radix dialog restores focus on
 *   close by itself; a panel has no focus trap and used to drop focus on <body> when it closed or
 *   unmounted while open. The trigger is focused only if it is still in the document (the same
 *   guard `Celebration`'s restore uses).
 * - ONE heading and ONE close control per panel. Every sheet body renders its own `.sheet-header`
 *   (Dialog.Title + text Close, both needed on the phone). The panel used to add a head of its
 *   own on top — title + "×" — and hide the body's Close by CSS, which printed the title twice
 *   ("Care" over "Feed Ember", "Item" over the item's name). Now the panel renders NO head: the
 *   body's header IS the panel head (styled as such by `.aside-panel-body > .sheet-header`), and
 *   `title` is only the region's accessible name — pass the body's own specific title so the
 *   landmark and the visible heading agree.
 */
export function Aside({ title, children, ...sheet }: AsideProps) {
  const studio = useContext(StudioContext);
  const asPanel = studio?.slotEl != null;
  const token = useRef({}).current;

  useEffect(() => {
    if (!asPanel) return;
    studio!.claim(token, sheet.open);
    return () => studio!.claim(token, false);
  }, [asPanel, sheet.open, studio, token]);

  // Panel-mode focus restore. Runs on the open→closed edge and on unmount-while-open; the ref is
  // read at that moment (not captured at open), so a trigger that re-rendered meanwhile still
  // gets focus. A ref is used for `wasOpen` rather than state so the cleanup sees the latest value.
  const wasOpen = useRef(false);
  const restoreRef = sheet.restoreFocusRef;
  useEffect(() => {
    if (!asPanel) return;
    const restore = () => {
      const el = restoreRef?.current;
      if (el && el.isConnected) el.focus();
    };
    if (wasOpen.current && !sheet.open) restore();
    wasOpen.current = sheet.open;
    return () => { if (wasOpen.current) restore(); };
    // `restore` reads the ref live; only the open edge and the mode should re-run this.
  }, [asPanel, sheet.open, restoreRef]);

  if (!asPanel) return <BottomSheet {...sheet}>{children}</BottomSheet>;
  if (!sheet.open) return null;
  return (
    <Dialog.Root open modal={false} onOpenChange={sheet.onOpenChange}>
      {createPortal(
        <section className="aside-panel" aria-label={title}>
          <div className={`aside-panel-body${sheet.contentClassName ? ` ${sheet.contentClassName}` : ''}`}>{children}</div>
        </section>,
        studio!.slotEl!,
      )}
    </Dialog.Root>
  );
}
